import { createSelector } from '@reduxjs/toolkit'
import { selectBusinessTravellerApprovalPolicyForUser } from './selectBusinessTravellerApprovalPolicyForUser'
import { selectBusinessTravellerAccommodationBudget } from './selectBusinessTravellerAccommodationBudget'
import { selectBusinessTravellerCurrentApprovalRequest } from './selectBusinessTravellerCurrentApprovalRequest'
import { getAccommodationItems } from 'checkout/selectors/view/hotels'
import { checkoutAccommodationOfferView } from 'checkout/selectors/view/accommodation'
import { calculateAccommodationBudget } from 'businessTraveller/utils/calculateTripRequestBudget'

/**
 * Selector to determine if we should show the inline trip request form
 * instead of the normal payment form.
 *
 * This applies to ALL accommodation bookings (both single and multi-room)
 * when approval is required and there's no existing approved trip request.
 *
 * Returns true while the approval policy is loading to prevent bypass using refreshing the page.
 */
export const selectShouldShowInlineTripRequestForm = createSelector(
  selectBusinessTravellerApprovalPolicyForUser,
  selectBusinessTravellerAccommodationBudget,
  getAccommodationItems,
  checkoutAccommodationOfferView,
  selectBusinessTravellerCurrentApprovalRequest,
  (state: App.State) => state.businessTraveller.businessApprovalPolicy.status,
  (
    approvalPolicy,
    accommodationBudget,
    accommodationItems,
    accommodationOfferViewResult,
    currentApprovalRequest,
    approvalPolicyStatus,
  ): boolean => {
    // Only handle accommodation for now
    if (accommodationItems.length === 0) {
      return false
    }

    // If the approved trip request exists (proceed with booking), show payment form
    if (currentApprovalRequest?.status === 'APPROVED') {
      return false
    }

    // If the approval policy is still loading, show the trip request form section (loading circle)
    if (approvalPolicyStatus === 'pending') {
      return true
    }

    // No approval policy means no trip request needed
    if (!approvalPolicy) {
      return false
    }

    // If scope is ALWAYS, we don't need offer data to know trip request is required
    if (approvalPolicy.scope === 'ALWAYS') {
      return true
    }

    // If scope is NEVER, no trip request needed
    if (approvalPolicy.scope === 'NEVER') {
      return false
    }

    // For OUTOFPOLICYONLY, we need offer data to calculate if over budget
    if (!accommodationOfferViewResult.hasRequiredData) {
      return true
    }

    // Calculate budget using the shared util
    const { isOutOfBudget } = calculateAccommodationBudget({
      accommodationItems,
      accommodationViews: accommodationOfferViewResult.data,
      accommodationBudget,
    })

    if (isOutOfBudget) {
      return true
    }

    return false
  },
)
